import { FC, ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { AppearedContent } from "../../DataDisplay/AppearedContent";
import { SubtitleItem } from ".";
import { ChevronDownIcon } from "../ChevronDownIcon";
import { LARGE_ICON_SIZE_STYLES } from "../../../ui/styles";
import { NamedSwitch } from "../../../ui/layout/molecules/NamedSwitch";

const subtitleStyles = "text-stone-500 dark:text-stone-400 truncate";

const renderCardIcon = (
  icon: ReactElement,
  isActive?: boolean,
  specialIconStyle?: string,
) => {
  return (
    <div
      className={`mr-2 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-colors ${
        specialIconStyle ??
        (isActive === false
          ? "bg-stone-300 text-white dark:bg-stone-600"
          : "bg-green-500  text-green-100")
      }`}
    >
      <span className={`${LARGE_ICON_SIZE_STYLES} justify-self-end`}>
        {icon}
      </span>
    </div>
  );
};

const renderSubtitle = (subtitle: SubtitleItem) => {
  return (
    <>
      {subtitle.type === "text" ? (
        <span className={subtitleStyles}>{subtitle.text}</span>
      ) : subtitle.type === "link" ? (
        <a
          className="w-full max-w-fit"
          href={subtitle.link ?? subtitle.text}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="block truncate text-stone-600 underline dark:text-stone-300">
            {subtitle.text}
          </span>
        </a>
      ) : (
        subtitle.type === "object" &&
        subtitle.items.map((item, index) => {
          return (
            <span key={index} className={`mr-3 ${subtitleStyles}`}>
              {item.title}
              <span className="ml-1 text-stone-900 dark:text-white">
                {item.value}
              </span>
            </span>
          );
        })
      )}
    </>
  );
};

const renderMainText = (
  appearingSubtitles: number,
  title?: string,
  subtitles?: SubtitleItem[],
) => {
  const appearingContentNumber =
    appearingSubtitles === 0 || !subtitles
      ? appearingSubtitles
      : appearingSubtitles - subtitles.length;
  return (
    <div className="ml-2 mr-2 flex flex-1 items-center justify-between truncate">
      <div className="text-primary flex w-full flex-col gap-1 pr-4 *:truncate">
        <AppearedContent
          isLoaded={title !== undefined}
          skeletonOptions={{
            amount: 1,
            skeletonStyle: "rowNormalStyle",
          }}
        >
          <p className="h-6 truncate text-base">{title}</p>
        </AppearedContent>

        {subtitles &&
          subtitles.map((subtitle, index) => {
            return (
              <p
                className="flex h-5 flex-row flex-wrap text-sm font-normal"
                key={index}
              >
                {renderSubtitle(subtitle)}
              </p>
            );
          })}

        {appearingContentNumber > 0 && (
          <AppearedContent
            isLoaded={false}
            skeletonOptions={{
              amount: appearingContentNumber,
              skeletonStyle: "rowSmStyle",
            }}
          />
        )}
      </div>
    </div>
  );
};

export type DetailedCardProps = {
  link?: string;
  icon?: ReactElement;
  title?: string;
  subtitles?: SubtitleItem[];
  onChange?: (value: boolean) => void;
  onClick?: () => void;
  isActive?: boolean;
  isExtended: boolean;
  canSelfExtend?: boolean;
  permissions?: string;
  appearingSubtitles?: number;
  isFilteredOut?: boolean;
  specialStyle?: string;
  specialIconStyle?: string;
  textAtRight?: string;
  iconAtRight?: ReactElement;
  buttonAtRight?: ReactElement;
  dataId?: string;
};
export const DetailedCard: FC<React.PropsWithChildren<DetailedCardProps>> = ({
  link,
  icon,
  title,
  subtitles,
  onChange,
  children,
  isActive,
  isExtended = false,
  canSelfExtend = false,
  permissions = "",
  appearingSubtitles = 0,
  isFilteredOut = false,
  specialStyle,
  specialIconStyle,
  onClick,
  textAtRight,
  iconAtRight,
  buttonAtRight,
  dataId,
}) => {
  const navigate = useNavigate();
  const [extendable, setExtendable] = useState(isExtended);

  useEffect(() => {
    setExtendable(isExtended);
  }, [isExtended]);

  return (
    <div
      className={
        isFilteredOut
          ? "hidden"
          : `items-center p-4 pr-3 transition duration-200 ease-in-out hover:bg-stone-100 dark:hover:bg-stone-700 ${
              (onClick || link) && " cursor-pointer "
            } ${specialStyle ?? "rounded-2xl bg-stone-50 dark:bg-stone-800 "}`
      }
      onClick={() => {
        link && navigate(link);
        onClick && onClick();
      }}
      {...(dataId ? { "data-id": dataId } : {})}
      {...(link ? { "data-href": link } : {})}
      data-card="detailed"
    >
      <div className="flex">
        {icon && renderCardIcon(icon, isActive, specialIconStyle)}
        {renderMainText(appearingSubtitles, title, subtitles)}
        {onChange && (
          <NamedSwitch
            title="show details"
            value={!!isActive}
            onChange={onChange}
            permissions={permissions}
            styleVariant="srLabel"
            className="my-auto"
          />
        )}
        {canSelfExtend && (
          <div className="flex items-center">
            <span className="sr-only">Show details</span>
            <span
              className={`rounded-2xl border border-stone-400 p-2 ${
                extendable ? "rotate-180" : ""
              }`}
            >
              <ChevronDownIcon />
            </span>
            <input
              type="checkbox"
              className="-ml-10 cursor-pointer p-5 opacity-0"
              onClick={(e) => {
                e.stopPropagation();
                setExtendable((e.target as HTMLInputElement).checked);
              }}
            />
          </div>
        )}
        {(textAtRight || iconAtRight || buttonAtRight) && (
          <div className="flex items-center gap-3 text-sm font-normal text-stone-500 dark:text-stone-400 ">
            {textAtRight}
            {iconAtRight}
            {buttonAtRight}
          </div>
        )}
      </div>
      {extendable && children}
    </div>
  );
};
